import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection as Section } from '../components/layout/LegalLayout';

const LAST_UPDATED = 'July 9, 2026';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated={LAST_UPDATED}
      back={{ to: '/register', label: 'Back to Registration' }}
    >
        <Section title="1. Acceptance of Terms">
          <p>
            By registering an account, purchasing a license, or using MortisEngine ("the Service"),
            you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these
            Terms, do not register, purchase, or use the Service.
          </p>
        </Section>

        <Section title="2. Nature of the Service">
          <p>
            MortisEngine provides third-party software designed to interact with Drakensang Online.
            This software is not affiliated with, endorsed by, or sponsored by the developers or
            publishers of Drakensang Online. Use of this software may violate the terms of service
            or end-user license agreement of Drakensang Online, and may result in actions taken
            against your game account by the game's operator, including but not limited to
            suspension or permanent ban, entirely outside of our control.
          </p>
          <p>
            You acknowledge and accept this risk entirely. We bear no responsibility for any
            consequences to your game account, hardware, or any other asset resulting from use
            of the Service.
          </p>
        </Section>

        <Section title="3. No Warranty">
          <p>
            The Service is provided "as is" and "as available," without any warranties of any kind,
            either express or implied, including but not limited to warranties of merchantability,
            fitness for a particular purpose, or non-infringement. We do not guarantee that the
            Service will be uninterrupted, error-free, undetected, or compatible with any specific
            game version or update.
          </p>
          <p>
            Game updates, patches, or anti-cheat changes may render the Service temporarily or
            permanently non-functional. We make no commitment regarding the timeline of fixes or
            updates following such changes.
          </p>
        </Section>

        <Section title="4. Refund Policy">
          <p>
            Refunds are <strong className="text-white">only considered</strong> if{' '}
            <strong className="text-white">all</strong> of the following conditions are met:
          </p>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-1">
            <li>The refund request is submitted within <strong className="text-white">14 days</strong> of the original purchase date.</li>
            <li>The license key has <strong className="text-white">not been activated</strong> (i.e., not bound to a Hardware ID).</li>
            <li>The associated software has <strong className="text-white">not been downloaded or launched</strong> using the provided key.</li>
          </ul>
          <p className="mt-3">
            If all conditions above are satisfied, you may contact our support to request a review.
            Approval is at our sole discretion and is not guaranteed. Refund requests that do not
            meet every condition listed above will be automatically denied.
          </p>
          <p className="mt-3">
            <strong className="text-white">No refunds will be issued under any of the following circumstances, regardless of the above:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-1">
            <li>The license key has been activated or the software has been downloaded or used in any way</li>
            <li>More than 14 days have passed since the purchase date</li>
            <li>Dissatisfaction with the Service's performance or features</li>
            <li>Detection, suspension, or banning of your game account</li>
            <li>Account suspension or termination on MortisEngine for any reason, including Terms violations</li>
            <li>Temporary unavailability of the Service due to game updates, patches, or maintenance</li>
            <li>Change of mind after the key has been accessed or used</li>
            <li>Hardware changes requiring a HWID reset</li>
          </ul>
        </Section>

        <Section title="5. Account Bans and Enforcement">
          <p>
            We reserve the right to suspend, freeze, or permanently ban any account at our sole
            discretion, for any reason, including suspected abuse, sharing of license keys,
            reverse engineering, chargeback fraud, or violation of these Terms or our{' '}
            <Link to="/rules" className="text-brand-400 hover:text-brand-300 underline">Rules &amp; Acceptable Use Policy</Link>.
          </p>
          <p>
            <strong className="text-white">Automated enforcement:</strong> The MortisEngine
            desktop application (the "Loader") includes automated systems that monitor for
            activity indicative of abuse, tampering, or attempts to circumvent licensing or
            security measures. Detection of such activity may result in an immediate, automatic
            suspension or ban of your account and license, without prior warning and without
            human review at the time of the action. The specific detection mechanisms are not
            disclosed.
          </p>
          <p>
            You may request a manual review of an automated ban through our support channels;
            however, we are under no obligation to reverse an automated decision, and submitting
            a review request does not guarantee reinstatement.
          </p>
          <p>
            <strong className="text-white">
              A ban or suspension of your MortisEngine account — whether issued manually by staff
              or automatically by the Loader — does not entitle you to any refund, partial or full,
              of any amount paid.
            </strong> Any remaining subscription time is forfeited upon a ban.
          </p>
        </Section>

        <Section title="6. Hardware ID (HWID) Locking">
          <p>
            Licenses are bound to a single hardware identifier (HWID) generated by the desktop
            application. You are responsible for ensuring the security of your own device and
            account. HWID resets may be granted at the discretion of our staff but are not
            guaranteed and may be limited in frequency.
          </p>
        </Section>

        <Section title="7. Account Security">
          <p>
            You are solely responsible for maintaining the confidentiality of your account
            credentials and license keys. We are not liable for any loss or damage arising from
            your failure to safeguard this information, including unauthorized use of your account.
          </p>
        </Section>

        <Section title="8. Service Availability">
          <p>
            We reserve the right to modify, suspend, or discontinue the Service, in whole or in
            part, at any time and without prior notice. We are not liable to you or any third party
            for any modification, suspension, or discontinuation of the Service.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, MortisEngine and its operators shall not be
            liable for any indirect, incidental, special, consequential, or punitive damages,
            including loss of profits, data, game accounts, or other intangible losses, resulting
            from your access to or use of, or inability to access or use, the Service.
          </p>
        </Section>

        <Section title="10. Invite System">
          <p>
            Invite codes granted through the referral/invite system are single-use and
            non-transferable for resale. Abuse of the invite system, including submitting false
            justifications for invite requests, may result in account suspension.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. Continued use of the Service after changes
            constitutes acceptance of the revised Terms. We encourage you to review this page
            periodically.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions regarding these Terms can be directed to our support channels.
          </p>
        </Section>
    </LegalLayout>
  );
}
